/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { Col, Input, Row, Space } from 'antd'
import {
  menuSupplierInvoiceFilterStatus,
  menuSupplierInvoiceStatus,
  menuSupplierOrderStatus,
  SupplierInvoiceStatus,
  SupplierOrderStatus,
} from 'helpers/constanst'
import {
  formatDateTime,
  formatDateWithoutTime,
  getSupplierInvoiceStatus,
  getSupplierOrderStatus,
  numberWithComma,
  toDateQuery,
} from 'helpers/format'
import { selectCurrentUser } from 'Store/user/selector'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { RangeDateItem, SelectItem } from 'Components/form'
import { PrimaryButton } from 'Components/button'
import { SearchOutlined } from '@ant-design/icons'
import { TableListPage } from 'Components/common/Table'
import { Pagination } from 'Components/Pagination'
import { ExpireDate, StatusTag } from 'Components/utilities'
import { cloneDeep } from 'lodash'
import OrderSummary from './OrderSummary'
import { getSaleOrderListAPI } from 'Store/saleorder-customer/api'
import { SaleOrderCustomerData, SaleOrderListData, SaleOrderListReq } from 'Store/saleorder-customer/types'
import { selectAllSaleOrderList, selectSaleOrderlEvent } from 'Store/saleorder-customer/selector'
import theme, { MyTheme } from 'Components/common/Theme'
import SupplierOrderDetail from '../../SupplierInvoice/detail'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  id?: number
  customer?: SaleOrderCustomerData
}

const Orders: React.FunctionComponent<Props> = props => {
  const initFilter = {
    start_date: '',
    end_date: '',
    keyword: '',
    status: '',
    payment_status: '',
    order_by: 'created_at',
    sort: 'desc',
  }

  const [filter, setFilter] = useState(cloneDeep(initFilter))
  const { onCloseDrawer, visible, onSuccess = () => {}, currentUser, list, listEvent, id, customer } = props
  const dispatch = useDispatch()
  const [detailDrawer, setDetailDrawer] = useState(false)
  const [selectedID, setSelectedID] = useState(0)

  const meta = list?.meta
  const datas = list?.saleorders
  const summary = list?.customer_summary
  const [pagination, setPagination] = useState({
    Page: 1,
    RowPerPage: 10,
  })

  const onClickSearch = () => {
    pagination.Page = 1
    setPagination({ ...pagination })
  }

  useEffect(() => {
    if (pagination.Page === 1) return
    pagination.Page = 1
    pagination.RowPerPage = 10
    setPagination({ ...pagination })
  }, [visible])

  const onGetList = (): void => {
    const req: SaleOrderListReq = {
      page: pagination.Page,
      per_page: pagination.RowPerPage,
      start_date: toDateQuery(filter.start_date),
      end_date: toDateQuery(filter.end_date),
      order_by: filter.order_by,
      sort: filter.sort,
      id: id || 0,
      keyword: filter.keyword,
      payment_status: filter.payment_status,
      status: filter.status,
    }
    dispatch(getSaleOrderListAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    if (visible && currentUser?.accessToken) {
      onGetList()
    }
  }, [pagination])

  useEffect(() => {
    pagination.Page = 1
    setPagination({ ...pagination })
  }, [filter?.order_by, filter?.sort])

  useEffect(() => {
    if (visible && currentUser?.accessToken) {
      onGetList()
    }

    return () => {
      setFilter(cloneDeep(initFilter))
    }
  }, [visible])

  const onFilter = (key: string, val: any) => {
    setFilter({
      ...filter,
      [key]: val,
    })
  }

  const onSort = (order_by: string, sort: string) => {
    setFilter({
      ...filter,
      sort,
      order_by,
    })
  }

  const handleChangeDate = (statDate: string, endDate: string) => {
    setFilter({
      ...filter,
      start_date: statDate,
      end_date: endDate,
    })
  }

  const onOpenDetailDrawer = () => {
    setDetailDrawer(true)
  }

  const onCloseDetailDrawer = () => {
    setDetailDrawer(false)
  }

  const onClickViewDetail = (id: number) => {
    setSelectedID(id)
    onOpenDetailDrawer()
  }

  const title = (
    <Space>
      <span>ชื่อลูกค้า/คู่ค้า : {customer?.name}</span>
    </Space>
  )

  const renderStatus = (status: string) => {
    const statusResult = getSupplierOrderStatus(status)
    return <StatusTag text={statusResult.text} color={statusResult.color} />
  }

  const renderPaymentStatus = (status: string) => {
    const statusResult = getSupplierInvoiceStatus(status)
    return <StatusTag text={statusResult.text} color={statusResult.color} />
  }

  const renderPaymentDate = (value: string, record: SaleOrderListData) => {
    const isPending =
      record.payment_status === SupplierInvoiceStatus.WAITING ||
      record.payment_status === SupplierInvoiceStatus.PATIAL
    return isPending && value ? (
      <ExpireDate date={value || ''} />
    ) : value ? (
      formatDateWithoutTime(value || '')
    ) : (
      '-'
    )
  }

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'no',
      key: 'no',
      render: (value: any, record: any, index: number) => {
        return index + 1 + (pagination.Page - 1) * pagination.RowPerPage
      },
      width: '60px',
    },
    {
      title: 'เลขที่สั่งซื้อ',
      dataIndex: 'sale_order_no',
      key: 'sale_order_no',
      width: '180px',
      render: (val: string, record: SaleOrderListData) => (
        <LinkNo onClick={() => onClickViewDetail(record.id)}>{val}</LinkNo>
      ),
      sorter: true,
    },
    {
      title: 'วันที่สั่งซื้อ',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val: string) => formatDateTime(val),
      sorter: true,
    },
    {
      title: 'วันที่ครบกำหนดชำระ',
      dataIndex: 'expiry_dt',
      key: 'expiry_dt',
      render: renderPaymentDate,
      sorter: true,
    },
    {
      title: 'ยอดสั่งซื้อ',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (val: number) => numberWithComma(val, true),
      align: 'right',
      sorter: true,
    },
    {
      title: 'ชำระแล้ว',
      dataIndex: 'total_partial_amount',
      key: 'total_partial_amount',
      render: (val: number) => numberWithComma(val, true),
      align: 'right',
      sorter: true,
    },
    {
      title: 'เงินคงค้าง',
      dataIndex: 'total_remain_amount',
      key: 'total_remain_amount',
      render: (val: number) => numberWithComma(val, true),
      align: 'right',
      sorter: true,
    },
    {
      title: '',
      dataIndex: '',
      key: 'space',
      width: '50px',
    },
    {
      title: 'สถานะคำสั่งซื้อ',
      dataIndex: 'status',
      key: 'status',
      render: renderStatus,
      sorter: true,
    },
    {
      title: 'สถานะชำระเงิน',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: renderPaymentStatus,
      sorter: true,
    },
  ]

  const FilterContent = (
    <FilterWrapper>
      <Row justify="space-between" align="middle" gutter={[8, 8]}>
        <Col xs={24} md={24}>
          <Row gutter={[8, 8]}>
            <Col xs={24} md={6}>
              <SearchInput
                placeholder="ค้นหาเลขที่คำสั่งซื้อ"
                prefix={<SearchOutlined />}
                allowClear
                value={filter.keyword}
                onChange={(evt: any): void => onFilter('keyword', evt.target.value)}
              />
            </Col>
            <Col xs={24} md={4}>
              <SelectItem
                name={'status'}
                label={''}
                isRequired={true}
                value={filter.status}
                menu={menuSupplierOrderStatus}
                placeHolder={'สถานะการขาย'}
                onChange={(val: string): any => onFilter('status', val)}
              />
            </Col>
            <Col xs={24} md={4}>
              <SelectItem
                name={'payment_status'}
                label={''}
                isRequired={true}
                value={filter.payment_status}
                menu={menuSupplierInvoiceFilterStatus}
                placeHolder={'สถานะการชำระเงิน'}
                onChange={(val: string): any => onFilter('payment_status', val)}
              />
            </Col>
            <Col xs={24} md={6}>
              <RangeDateItem
                placeHolder={['วันที่สั่งซื้อ', 'สิ้นสุด']}
                onChange={handleChangeDate}
                startDate={filter.start_date}
                endDate={filter.end_date}
              />
            </Col>
            <Col>
              <PrimaryButton text="ค้นหา" btnType="Cancel" onClick={onClickSearch} />
            </Col>
          </Row>
        </Col>
      </Row>
    </FilterWrapper>
  )

  const PaginationContent = (
    <Pagination
      currentPage={pagination.Page}
      totalRows={meta?.totalCount || 0}
      perPage={pagination.RowPerPage}
      onClickPage={(page: number) => {
        pagination.Page = page
        setPagination({ ...pagination })
      }}
      onChangeNumber={(number: number) => {
        pagination.RowPerPage = number
        setPagination({ ...pagination })
      }}
    />
  )

  const SupplierOrdeDetailDrawer = (
    <SupplierOrderDetail
      id={selectedID}
      visible={detailDrawer}
      onCloseDrawer={onCloseDetailDrawer}
      onSuccess={onSuccess}
      viewMode={true}
    />
  )

  return (
    <DetailDrawer title={title} onCloseDrawer={onCloseDrawer} visible={visible} footer={null} width={'80%'}>
      {SupplierOrdeDetailDrawer}
      <Row justify="space-between" gutter={[16, 8]}>
        <Col span={24}>
          <OrderSummary detail={summary} />
        </Col>
        <Col span={24}>
          <BoxTitle>รายการสั่งซื้อ</BoxTitle>
        </Col>
        <Col span={24}>{FilterContent}</Col>

        <Col span={24}>
          <TableListPage
            columns={columns}
            datas={datas || []}
            isLoading={listEvent.IsLoading}
            paginationContent={PaginationContent}
            bordered={false}
            rowKey="id"
            onSort={(field: string, order: string) => {
              onSort(field, order)
            }}
            size="small"
          />
        </Col>
      </Row>
    </DetailDrawer>
  )
}

const FilterWrapper = styled.div`
  padding: 5px 0 5px 0;
`

const SearchInput = styled(Input)`
  border-radius: 6px;
  height: 45px;
`
const LinkNo = styled.div`
  cursor: pointer;
  text-decoration: underline;
  font-family: ${theme.fontFamily.bold};
  color: ${theme.colors.primary};
`
const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body2};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    list: selectAllSaleOrderList(state),
    listEvent: selectSaleOrderlEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(Orders)
